/**
 * Enhanced Devices List Component
 * Custom DataGrid styling with filters and modern UI
 */

import React, { useEffect, useState } from 'react';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Link } from 'react-router-dom';
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Tooltip,
  Typography,
  styled,
  Chip,
} from '@mui/material';

import { getAllDevices, deleteDevice } from '../api/devices';
import AddDeviceForm from './AddDeviceForm';
import { Button } from './ui/Button';
import { StatusBadge } from './ui/StatusBadge';
import { LoadingSkeleton } from './ui/LoadingSkeleton';
import { DeviceFilters } from './devices/DeviceFilters';
import { colors } from '../theme/colors';

interface DeviceData {
  id: string;
  device_family_name: string;
  device_owner_id: string;
  adding_time: string;
  last_request_time: string;
  activation_time: string;
  is_active: boolean;
  device_api_key: string;
  device_activation_key: string;
  description: string;
}

interface GroupHeaderRow {
  id: string;
  isGroupHeader: true;
  familyName: string;
  deviceCount: number;
}

type TableRow = DeviceData | GroupHeaderRow;

function isGroupHeader(row: TableRow): row is GroupHeaderRow {
  return 'isGroupHeader' in row && row.isGroupHeader === true;
}

// Styled DataGrid with custom theme
const StyledDataGrid = styled(DataGrid)({
  border: `1px solid ${colors.neutral[200]}`,
  borderRadius: 16,
  backgroundColor: colors.background.paper,
  boxShadow: 'none',
  fontFamily: 'inherit',
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: colors.neutral[50],
    color: colors.text.primary,
    borderBottom: `2px solid ${colors.neutral[200]}`,
    borderRadius: 0,
    fontSize: '0.8125rem',
    fontWeight: 600,
    letterSpacing: '0.02em',
    textTransform: 'uppercase',
    minHeight: '56px !important',
    maxHeight: '56px !important',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 600,
    color: colors.text.secondary,
  },
  '& .MuiDataGrid-columnSeparator': {
    display: 'none',
  },
  '& .MuiDataGrid-row': {
    transition: 'all 0.15s ease-in-out',
    cursor: 'pointer',
    minHeight: '72px !important',
    '&:hover': {
      backgroundColor: colors.neutral[50],
      boxShadow: 'inset 3px 0 0 0 ' + colors.primary.main,
    },
  },
  '& .MuiDataGrid-cell': {
    borderBottom: `1px solid ${colors.neutral[100]}`,
    display: 'flex',
    alignItems: 'center',
    padding: '16px',
    fontSize: '0.9375rem',
    '&:focus': {
      outline: 'none',
    },
    '&:focus-within': {
      outline: 'none',
    },
  },
  '& .MuiDataGrid-footerContainer': {
    borderTop: `2px solid ${colors.neutral[200]}`,
    backgroundColor: colors.neutral[50],
    minHeight: '60px',
  },
  '& .MuiTablePagination-root': {
    color: colors.text.secondary,
  },
  '& .MuiDataGrid-selectedRowCount': {
    display: 'none',
  },
});

const HeaderBox = styled(Box)({
  padding: '32px 0',
  marginBottom: 32,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

const ExpandIcon = styled(ExpandMoreIcon, {
  shouldForwardProp: (prop) => prop !== 'expanded',
})<{ expanded: boolean }>(({ expanded }) => ({
  transition: 'transform 0.15s ease-out',
  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
  color: colors.text.secondary,
}));

const DevicesListEnhanced: React.FC = () => {
  const [data, setData] = useState<DeviceData[]>([]);
  const [filteredData, setFilteredData] = useState<DeviceData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAddDeviceDialogOpen, setIsAddDeviceDialogOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [sortModel, setSortModel] = useState<any[]>([]);

  // Create flat array with group headers and devices
  const tableRows = React.useMemo(() => {
    // If sorting is active, return flat list without group headers
    if (sortModel.length > 0) {
      return filteredData;
    }

    // Otherwise, create grouped view with headers
    const rows: TableRow[] = [];
    const groups: Record<string, DeviceData[]> = {};

    // Group devices by family
    filteredData.forEach(device => {
      const family = device.device_family_name;
      if (!groups[family]) {
        groups[family] = [];
      }
      groups[family].push(device);
    });

    // Sort families alphabetically and create flat structure
    Object.keys(groups)
      .sort((a, b) => a.localeCompare(b))
      .forEach(family => {
        const devices = groups[family];

        // Add group header row
        rows.push({
          id: `group-header-${family}`,
          isGroupHeader: true,
          familyName: family,
          deviceCount: devices.length,
        } as GroupHeaderRow);

        // Add devices if section is expanded
        if (expandedSections[family] !== false) {
          rows.push(...devices);
        }
      });

    return rows;
  }, [filteredData, expandedSections, sortModel]);

  // Initialize all sections as expanded on first load
  React.useEffect(() => {
    const families = new Set(filteredData.map(d => d.device_family_name));
    setExpandedSections(prev => {
      const newExpanded = { ...prev };
      let hasChanges = false;
      families.forEach(family => {
        if (newExpanded[family] === undefined) {
          newExpanded[family] = true;
          hasChanges = true;
        }
      });
      return hasChanges ? newExpanded : prev;
    });
  }, [filteredData.length]);

  const toggleSection = React.useCallback((family: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [family]: !prev[family],
    }));
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const response = await getAllDevices();
      setData(response || []);
      setFilteredData(response || []);
    } catch (error) {
      console.error('Error fetching devices:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (filtered: DeviceData[]) => {
    setFilteredData(filtered);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleDelete = async (device_id: string) => {
    const confirmed = window.confirm(`Вы уверены, что хотите удалить устройство ${device_id}?`);
    if (confirmed) {
      try {
        setIsLoading(true);
        await deleteDevice(device_id);
        await fetchData();
      } catch (error) {
        console.error('Error deleting device:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const renderStatusBadge = (params: GridRenderCellParams) => {
    const status = params.row.is_active as boolean;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
        <StatusBadge status={status ? 'active' : 'inactive'} label={status ? 'Активно' : 'Неактивно'} />
      </Box>
    );
  };

  const renderAnalyticsButton = (params: GridRenderCellParams) => {
    const id = params.row.id;
    return (
      <Tooltip title="Просмотреть аналитику">
        <IconButton
          component={Link}
          to={`/device-data?device_id=${id}`}
          size="small"
          sx={{
            color: colors.secondary.main,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              color: colors.secondary.dark,
              transform: 'scale(1.1)',
              backgroundColor: 'rgba(0, 217, 255, 0.1)',
            },
          }}
        >
          <InfoIcon />
        </IconButton>
      </Tooltip>
    );
  };

  const renderDeleteButton = (params: GridRenderCellParams) => (
    <Tooltip title="Удалить устройство">
      <IconButton
        onClick={() => handleDelete(params.row.id as string)}
        disabled={isLoading}
        size="small"
        sx={{
          color: colors.error.main,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            color: colors.error.dark,
            transform: 'scale(1.1)',
            backgroundColor: 'rgba(255, 107, 107, 0.1)',
          },
        }}
      >
        <DeleteIcon />
      </IconButton>
    </Tooltip>
  );

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'Устройство',
      flex: 1,
      minWidth: 280,
      renderCell: (params) => {
        if (isGroupHeader(params.row)) {
          return (
            <Box
              onClick={() => toggleSection(params.row.familyName)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                cursor: 'pointer',
                py: 1,
                width: '100%',
              }}
            >
              <ExpandIcon expanded={expandedSections[params.row.familyName] !== false} />
              <Typography
                sx={{
                  fontSize: '1.125rem',
                  fontWeight: 600,
                  color: colors.text.primary,
                }}
              >
                {params.row.familyName}
              </Typography>
              <Chip
                label={`${params.row.deviceCount} ${params.row.deviceCount === 1 ? 'устройство' : params.row.deviceCount < 5 ? 'устройства' : 'устройств'}`}
                size="small"
                sx={{
                  backgroundColor: colors.primary.main,
                  color: 'white',
                  fontWeight: 500,
                  fontSize: '0.8125rem',
                }}
              />
            </Box>
          );
        }

        return (
          <Box>
            <Typography
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.875rem',
                color: colors.text.primary,
                fontWeight: 500,
                mb: 0.25,
              }}
            >
              {params.value}
            </Typography>
            <Typography
              sx={{
                fontSize: '0.8125rem',
                color: colors.text.secondary,
              }}
            >
              {params.row.description || 'Без описания'}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'device_family_name',
      headerName: 'Семейство',
      width: 160,
      renderCell: (params) => {
        if (isGroupHeader(params.row)) return null;
        return (
          <Box
            sx={{
              px: 1.5,
              py: 0.75,
              borderRadius: 1.5,
              backgroundColor: colors.neutral[100],
              display: 'inline-block',
            }}
          >
            <Typography
              sx={{
                fontSize: '0.8125rem',
                fontWeight: 500,
                color: colors.text.primary,
              }}
            >
              {params.value}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'is_active',
      headerName: 'Статус',
      width: 130,
      renderCell: (params) => {
        if (isGroupHeader(params.row)) return null;
        return renderStatusBadge(params);
      },
    },
    {
      field: 'last_request_time',
      headerName: 'Последняя активность',
      width: 200,
      renderCell: (params) => {
        if (isGroupHeader(params.row)) return null;

        const date = params.value ? new Date(params.value) : null;
        const now = new Date();
        const diffHours = date ? Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60)) : null;

        let timeText = '—';
        let textColor = colors.text.secondary;

        if (date) {
          if (diffHours < 1) {
            timeText = 'Менее часа назад';
            textColor = colors.success.main;
          } else if (diffHours < 24) {
            timeText = `${diffHours} ч. назад`;
            textColor = colors.text.primary;
          } else {
            const diffDays = Math.floor(diffHours / 24);
            timeText = `${diffDays} дн. назад`;
            textColor = colors.text.secondary;
          }
        }

        return (
          <Typography
            sx={{
              fontSize: '0.875rem',
              color: textColor,
              fontWeight: diffHours < 1 ? 500 : 400,
            }}
          >
            {timeText}
          </Typography>
        );
      },
    },
    {
      field: 'activation_time',
      headerName: 'Активирован',
      width: 160,
      renderCell: (params) => {
        if (isGroupHeader(params.row)) return null;
        return (
          <Typography
            sx={{
              fontSize: '0.875rem',
              color: colors.text.secondary,
            }}
          >
            {params.value ? new Date(params.value).toLocaleDateString('ru-RU', {
              day: 'numeric',
              month: 'short',
              year: 'numeric',
            }) : '—'}
          </Typography>
        );
      },
    },
    {
      field: 'analytics',
      headerName: '',
      width: 100,
      sortable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => {
        if (isGroupHeader(params.row)) return null;
        return (
          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
            {renderAnalyticsButton(params)}
            {renderDeleteButton(params)}
          </Box>
        );
      },
    },
  ];

  const openAddDeviceDialog = () => {
    setIsAddDeviceDialogOpen(true);
  };

  const closeAddDeviceDialog = () => {
    setIsAddDeviceDialogOpen(false);
  };

  if (isLoading && data.length === 0) {
    return (
      <Box sx={{ m: 4 }}>
        <LoadingSkeleton type="table" rows={8} />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: '1600px', mx: 'auto', px: 4, py: 5 }}>
      {/* Header with Add Button */}
      <HeaderBox>
        <Box>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 600,
              color: colors.text.primary,
              mb: 1,
              fontSize: '2rem',
            }}
          >
            Устройства
          </Typography>
          <Typography
            sx={{
              fontSize: '0.9375rem',
              color: colors.text.secondary,
              fontWeight: 400,
            }}
          >
            {filteredData.length} {filteredData.length !== data.length && `из ${data.length}`}
          </Typography>
        </Box>
        <Button variant="gradient" startIcon={<AddIcon />} onClick={openAddDeviceDialog}>
          Добавить устройство
        </Button>
      </HeaderBox>

      {/* Filters */}
      <DeviceFilters devices={data} onFilterChange={handleFilterChange} />

      {/* Single Data Grid with Group Headers */}
      <Box sx={{ width: '100%' }}>
        {filteredData.length === 0 && !isLoading ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 400,
              background: colors.background.paper,
              border: `1px solid ${colors.neutral[200]}`,
              borderRadius: 4,
              p: 8,
            }}
          >
            <Typography
              sx={{
                fontSize: '1.125rem',
                fontWeight: 600,
                color: colors.text.primary,
                mb: 1,
              }}
            >
              Нет устройств
            </Typography>
            <Typography
              sx={{
                fontSize: '0.9375rem',
                color: colors.text.secondary,
                mb: 4,
              }}
            >
              {data.length === 0 ? 'Добавьте первое устройство для начала работы' : 'Нет устройств, соответствующих фильтрам'}
            </Typography>
            {data.length === 0 && (
              <Button variant="gradient" startIcon={<AddIcon />} onClick={openAddDeviceDialog}>
                Добавить устройство
              </Button>
            )}
          </Box>
        ) : (
          <StyledDataGrid
            rows={tableRows}
            columns={columns}
            sortingMode="client"
            sortModel={sortModel}
            onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 50 },
              },
            }}
            pageSizeOptions={[25, 50, 100, 200]}
            disableRowSelectionOnClick
            disableColumnMenu
            loading={isLoading}
            autoHeight
            getRowHeight={() => 'auto'}
            getRowClassName={(params) => {
              return isGroupHeader(params.row) ? 'group-header-row' : '';
            }}
            sx={{
              '& .MuiDataGrid-row': {
                minHeight: '72px !important',
              },
              '& .group-header-row': {
                backgroundColor: `${colors.neutral[50]} !important`,
                borderTop: `2px solid ${colors.neutral[200]}`,
                borderBottom: `2px solid ${colors.neutral[200]}`,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: `${colors.neutral[100]} !important`,
                  boxShadow: 'none',
                },
              },
              '& .group-header-row .MuiDataGrid-cell': {
                borderBottom: 'none',
              },
            }}
          />
        )}
      </Box>

      {/* Add Device Dialog */}
      <Dialog open={isAddDeviceDialogOpen} onClose={closeAddDeviceDialog} maxWidth="sm" fullWidth>
        <DialogContent sx={{ p: 4 }}>
          <AddDeviceForm onSuccess={fetchData} />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default DevicesListEnhanced;
